"use client";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, {
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Presentation, render } from "react-pptx";
import OverallBarChart from "../OverallBarChart";
import { ActivityData} from "../../../../types/chartData";
import { PercentageHistogramChart } from "../PercentageHistogramChart.js.js";
import { LoadsRadarChart } from "../LoadsRadarChart";
import OverallComparisonBarChart from "../OverallComparisonBarChart";
import PercChangeHistogramChart from "../PercChangeHistogram";
import { ActivityMLIPainSlide } from "./ActivityMLIPainSlide";

type SlidesProps = {
  projectName: string;
  compProjectName: string;
  activity1: ActivityData;
  activity2: ActivityData;
  distributionChartB64: string;
  distributionPainChartB64: string;
  radarChartB64: string;
  startDate: Date;
  endDate: Date;
  compStartDate: Date;
  compEndDate: Date;
};
const Slides: React.FC<SlidesProps> = ({
  projectName,
  compProjectName,
  activity1,
  activity2,
  distributionChartB64,
  distributionPainChartB64,
  radarChartB64,
  startDate,
  endDate,
  compStartDate,
  compEndDate,
}) => {
  return (
    <>
      <ActivityMLIPainSlide
        projectName={projectName}
        compProjectName={compProjectName}
        distributionPainChartB64={distributionPainChartB64}
        startDate={startDate}
        endDate={endDate}
        compStartDate={compStartDate}
        compEndDate={compEndDate}
        activityData1={activity1}
        activityData2={activity2}
        distributionChartB64={distributionChartB64}
        radarChartB64={radarChartB64}
      />
    </>
  );
};

type GenerateDashboardPPTXPopupProps = {
  props: any;
  open: boolean;
  handleClose: () => void;
};

export type ChartRef = {
  overallBarChartB64: string;
  overallBarChartRef: any;
  distributionChartB64: string;
  distributionChartRef: any;
  radarChartB64: string;
  radarChartRef: any;
  distributionPainChartB64: string;
  distributionPainChartRef: any;
};

const GenerateDashboardPPTXPopup: React.FC<GenerateDashboardPPTXPopupProps> = ({
  props,
  open,
  handleClose,
}) => {
  const [activityCharts, setActivityCharts] = useState<ReactElement[]>([]);

  const renderedCount = useRef(0);
  const allChartsRendered = useRef(false);
  const [chartsReady, setChartsReady] = useState(false);
  const chartReferences = useRef<Map<number, ChartRef>>(new Map());

  const handleChartRendered = () => {
    renderedCount.current += 1;
    console.log(
      `Chart rendered: ${renderedCount.current}/${chartReferences.current.size}`
    );
    if (
      renderedCount.current === chartReferences.current.size &&
      !allChartsRendered.current
    ) {
      allChartsRendered.current = true;

      chartReferences.current.forEach((x) => {
        x.overallBarChartB64 = x.overallBarChartRef.toBase64Image(1);
        x.distributionChartB64 = x.distributionChartRef.toBase64Image(1);
        x.distributionPainChartB64 =
          x.distributionPainChartRef.toBase64Image(1);
        x.radarChartB64 = x.radarChartRef.toBase64Image(1);
      });
      setChartsReady(true);
    }
  };

  useEffect(() => {
    const charts: ReactElement[] = [];

    // if (activity1.average == null) return;

    chartReferences.current.set(1, {
      overallBarChartB64: "",
      overallBarChartRef: "",
      distributionChartB64: "",
      distributionChartRef: "",
      distributionPainChartB64: "",
      distributionPainChartRef: "",
      radarChartB64: "",
      radarChartRef: undefined,
    });

    charts.push(
      <Box key={1}>
        {props.activityData && !props.compActivityData && (
          <Box height={200} width={300}>
            <OverallBarChart
              average={props.activityData && props.activityData.average}
              height={200}
              selection={props.selectedActivity}
              attribute={"MLI"}
              selectAll={true}
              onRendered={handleChartRendered}
              ref={(ref) => {
                if (ref) {
                  const chartRef = chartReferences.current.get(1);
                  if (chartRef) {
                    chartRef.overallBarChartRef = ref;
                  }
                }
              }}
            />
          </Box>
        )}
        {props.activityData && props.compActivityData && (
          <OverallComparisonBarChart
            height={"300px"}
            data1={props.activityData && props.activityData.average}
            data2={props.compActivityData && props.compActivityData.average}
            pc1={props.selectedProject.projectCode}
            pc2={props.compProject?.projectCode as string}
            attribute={"MLI"}
            onRendered={handleChartRendered}
            ref={(ref) => {
              if (ref) {
                const chartRef = chartReferences.current.get(1);
                if (chartRef) {
                  chartRef.overallBarChartRef = ref;
                }
              }
            }}
          />
        )}
        <Box height={500} width={500}>
          {props.selectedActivity.activityName}
          <LoadsRadarChart
            height={500}
            selection={props.selectedActivity}
            deviceType={props.selectedProject.deviceType[0]}
            data1={
              props.activityData.deviceLoads && props.activityData.deviceLoads
            }
            data2={props.compActivityData && props.compActivityData.deviceLoads}
            pc1={props.selectedProject.projectCode}
            pc2={props.compProject?.projectCode}
            ref={(ref) => {
              if (ref) {
                const chartRef = chartReferences.current.get(1);
                if (chartRef) {
                  chartRef.radarChartRef = ref;
                }
              }
            }}
          />
        </Box>
        <Box>
          {!props.compActivityData && (
            <>
              <Box height={200} width={400}>
                <PercentageHistogramChart
                  height={200}
                  data1={
                    props.activityData.percentageHistogram &&
                    props.activityData.percentageHistogram
                  }
                  attribute={"MLI"}
                  selection={props.selectedActivity}
                  ref={(ref) => {
                    if (ref) {
                      const chartRef = chartReferences.current.get(1);
                      if (chartRef) {
                        chartRef.distributionChartRef = ref;
                      }
                    }
                  }}
                />
              </Box>
              <Box height={200} width={400}>
                <PercentageHistogramChart
                  height={200}
                  data1={
                    props.activityData.percentageHistogram &&
                    props.activityData.percentageHistogram
                  }
                  attribute={"Pain"}
                  selection={props.selectedActivity}
                  ref={(ref) => {
                    if (ref) {
                      const chartRef = chartReferences.current.get(1);
                      if (chartRef) {
                        chartRef.distributionPainChartRef = ref;
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}
          {props.compActivityData && (
            <Box height={200} width={400}>
              <PercChangeHistogramChart
                height={"200px"}
                data1={
                  props.activityData.percentageHistogram &&
                  props.activityData.percentageHistogram
                }
                data2={
                  props.compActivityData.percentageHistogram &&
                  props.compActivityData.percentageHistogram
                }
                attribute={"MLI"}
                ref={(ref) => {
                  if (ref) {
                    const chartRef = chartReferences.current.get(1);
                    if (chartRef) {
                      chartRef.distributionChartRef = ref;
                    }
                  }
                }}
              />
              <PercChangeHistogramChart
                height={"200px"}
                data1={
                  props.activityData.percentageHistogram &&
                  props.activityData.percentageHistogram
                }
                data2={
                  props.compActivityData.percentageHistogram &&
                  props.compActivityData.percentageHistogram
                }
                attribute={"Pain"}
                ref={(ref) => {
                  if (ref) {
                    const chartRef = chartReferences.current.get(1);
                    if (chartRef) {
                      chartRef.distributionPainChartRef = ref;
                    }
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    );

    setActivityCharts(charts);
  }, []);

  const handleDownload = async () => {
    try {
      const buffer = await render(
        <Presentation>
          <Slides
            projectName={props.selectedProject.projectName}
            compProjectName={props.compProject && props.compProject.projectName}
            distributionPainChartB64={
              chartReferences.current.get(1)?.distributionPainChartB64 ?? ""
            }
            startDate={props.startDate}
            endDate={props.endDate}
            compStartDate={props.compStartdate}
            compEndDate={props.compEnddate}
            activity1={props.activityData}
            activity2={props.compActivityData}
            distributionChartB64={
              chartReferences.current.get(1)?.distributionChartB64 ?? ""
            }
            radarChartB64={chartReferences.current.get(1)?.radarChartB64 ?? ""}
          />
        </Presentation>,
        { outputType: "blob" }
      );

      const url = URL.createObjectURL(buffer as Blob);
      const link = document.createElement("a");
      link.href = url;

      var name = "";
      if (props.compActivityData) {
        name = `${props.selectedProject.projectCode}-${props.compProject.projectCode}.pptx`;
      } else {
        name = `${props.selectedProject.projectCode}.pptx`;
      }
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating presentation:", error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Generate Powerpoint</DialogTitle>
      <DialogContent>
        {chartsReady && (
          <Button onClick={handleDownload} variant="contained">
            Download
          </Button>
        )}
        {activityCharts.map((c, i) => {
          return c;
        })}
      </DialogContent>
    </Dialog>
  );
};

export default GenerateDashboardPPTXPopup;
